import {
  Button,
  DropdownButton,
  FlexBox,
  SearchInput,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useArticlesFilters } from 'collection/pages/articles/hooks/useArticlesFilters';
import { useAtomValue } from 'jotai';
import {
  CollectionFieldsFragment,
  CollectionTreeQuery,
} from 'lib-knowledge-management-common/graphql';
import { usePaginatedCollections } from 'lib-knowledge-management-common/hooks/usePaginatedCollections';
import { Categories } from 'lib-knowledge-management-common/models/types';
import { useModal } from 'lib-knowledge-modal';
import Page from 'lib-knowledge-utils/components/page/index';
import { isValidSearchLength } from 'lib-knowledge-utils/string';
import { collectionIdAtom } from 'models/collectionStore';
import { useNavigate } from 'react-router-dom';
import { noop } from 'utils';

import { useDebouncedSearchValueInUrl } from 'hooks/useDebouncedSearchValueInUrl';

import { CollectionArticlesTable } from '../articlesTable';
import { CollectionSidebar } from '../collectionSidebar';
import CollectionTree from '../collectionTree';
import { CategoryAddModal } from '../collectionTree/components/categoryNode/modals/categoryAddModal';
import { DropdownContent } from '../editDeleteDropdown/DropdownContent';
import { ArticleAddModal } from '../modals/components/articleAddModal';
import {
  CollectionEditPagePlaceholder,
  TopBarPlaceholder,
} from '../placeholders/collectionEditPagePlaceholder';
import { CollectionSidebarPlaceholder } from '../placeholders/sidebarPlaceholder';

import styles from './layout.module.scss';

export function CollectionManagementNav(): JSX.Element | null {
  const navigate = useNavigate();
  const { data, loading, hasMore, fetchNextPage } = usePaginatedCollections({
    fetchPolicy: 'cache-and-network',
  });

  if (!data && loading) return <CollectionSidebarPlaceholder />;
  if (!data) return null;

  return (
    <CollectionSidebar
      hasMore={hasMore}
      fetchMore={fetchNextPage}
      collectionLinks={data.collections.results.map((result) => ({
        id: result.id,
        title: result.title,
        internalKbEnabled: result.internalKbEnabled,
        internalOnly: result.internalOnly,
        isSyncedWithElevio: result.isSyncedWithElevio,
        onClick: (id: string) => {
          navigate(`/knowledge/collection/${id}`);
        },
      }))}
    />
  );
}

function Subtitle({
  linkedResources,
  internalOnly,
}: {
  linkedResources: CollectionFieldsFragment['linkedResources'];
  internalOnly: boolean;
}) {
  const queues = linkedResources
    .map((resource) => resource.name)
    .join(', ')
    .toString();

  let queueCount = 'No queues';

  if (linkedResources.length === 1) {
    queueCount = '1 queue';
  } else if (linkedResources.length > 1) {
    queueCount = `${linkedResources.length} queues`;
  }

  return (
    <>
      {internalOnly && 'Internal only · '}
      <Tooltip content={queues}>{queueCount}</Tooltip>
    </>
  );
}

type CollectionLayoutProps = {
  loading: boolean;
  data: CollectionTreeQuery | undefined;
};
const CollectionLayout = ({
  loading,
  data,
}: CollectionLayoutProps): JSX.Element => {
  return (
    <Page>
      <Page.Body>
        <CollectionManagementNav />
        <FlexBox flexDirection="column" width="100%">
          <PageBodyContent data={data} loading={loading} />
        </FlexBox>
      </Page.Body>
    </Page>
  );
};
export default CollectionLayout;

type PageBodyContentProps = Pick<CollectionLayoutProps, 'data'> & {
  loading: boolean;
};
function PageBodyContent({ data, loading }: PageBodyContentProps) {
  const { instantSearchValue, onChange } = useDebouncedSearchValueInUrl();
  const { store } = useArticlesFilters();

  if (!data && loading)
    return (
      <>
        <TopBarPlaceholder />
        <CollectionEditPagePlaceholder />
      </>
    );

  const linkedResources = data?.collection?.linkedResources || [];
  const subtitle = (
    <Subtitle
      linkedResources={linkedResources}
      internalOnly={data?.collection?.internalOnly ?? false}
    />
  );

  const searchTerm = store.search || '';
  const isValidSearch = isValidSearchLength(searchTerm);

  const categories = data?.collection?.categories;
  const collectionTitle = data?.collection?.title;
  const isSyncedWithElevio = data?.collection?.isSyncedWithElevio ?? false;

  return (
    <>
      <Page.Head
        className={styles.head}
        slotLeft={
          <Page.HeadTitle title={collectionTitle || ''} subtitle={subtitle} />
        }
        slotRight={
          <FlexBox gap="200">
            <SearchInput
              onChange={onChange}
              initialSearch={instantSearchValue || undefined}
              onSubmit={noop}
            />
            <DropdownButton
              icon="dots-h"
              noArrow
              dropdownProps={{ closeOnBlur: true }}
              dropdown={
                <Dropdown
                  collectionTitle={collectionTitle}
                  isSyncedWithElevio={isSyncedWithElevio}
                />
              }
            />
            <ActionButtonGroup
              isSyncedWithElevio={isSyncedWithElevio}
              categories={categories}
            />
          </FlexBox>
        }
      />
      <CollectionDisplay isValidSearch={isValidSearch} data={data} />
    </>
  );
}

function Dropdown({
  collectionTitle,
  isSyncedWithElevio,
}: {
  collectionTitle: string | undefined;
  isSyncedWithElevio: boolean;
}): JSX.Element {
  const collectionId = useAtomValue(collectionIdAtom);

  if (collectionId && !!collectionTitle) {
    return (
      <DropdownContent
        collectionTitle={collectionTitle}
        collectionId={collectionId}
        isSyncedWithElevio={isSyncedWithElevio}
      />
    );
  }

  return <></>;
}

type ActionButtonGroupProps = {
  isSyncedWithElevio: boolean;
  categories: Categories | undefined;
};
function ActionButtonGroup({
  isSyncedWithElevio,
  categories,
}: ActionButtonGroupProps) {
  const { showModal } = useModal();

  if (isSyncedWithElevio) return null; // Cannot add content if the collection is synced

  return (
    <>
      <Button
        variant="secondary"
        onClick={() =>
          showModal(CategoryAddModal, {
            presetParent: undefined,
          })
        }
      >
        New category
      </Button>
      <NewArticleActionButton categories={categories} />
    </>
  );
}

type NewArticleActionButtonProps = {
  categories: Categories | undefined;
};

function NewArticleActionButton({ categories }: NewArticleActionButtonProps) {
  const { showModal } = useModal();

  if (!categories?.length) return null;
  return (
    <Button
      onClick={() => {
        showModal(ArticleAddModal, {
          presetParent: undefined,
        });
      }}
    >
      New article
    </Button>
  );
}

function CollectionDisplay({
  isValidSearch,
  data,
}: {
  isValidSearch: boolean;
  data: CollectionTreeQuery | undefined;
}) {
  if (isValidSearch) return <CollectionArticlesTable />;

  return <CollectionTree collectionData={data} />;
}
